import { z } from 'zod';

import { type RouteShiftStatus } from '@apps/web/components/dashboard/shift_list_filters';
import { type ShiftsPresentation } from '@apps/web/components/dashboard/shifts_presentation';
import { type CalendarViewMode } from '@apps/web/components/shifts_calendar/calendar_model';
import { isValidDateKey } from '@apps/web/lib/date_utils';

/**
 * The shifts route's search contract, owned in one place so the table and its
 * filter hook cannot drift apart.
 *
 * `shifts_route_search_test` pins this list against `shiftsSearchSchema`, so a
 * new search key fails that test until someone decides whether table
 * navigations should carry it. Without that, a key added to the route is
 * silently dropped by every navigation the table issues.
 */
export const SHIFT_ROUTE_SEARCH_KEYS = [
    'approve',
    'calloutShiftId',
    'chatId',
    'chatMode',
    'clientIds',
    'date',
    'from',
    'mode',
    'organizationId',
    'presentation',
    'scope',
    'shiftId',
    'slackWebClaim',
    'status',
    'to',
    'view',
] as const;

export type ShiftRouteSearchKey = (typeof SHIFT_ROUTE_SEARCH_KEYS)[number];

export type ShiftRouteSearch = {
    readonly approve?: boolean;
    readonly calloutShiftId?: string;
    readonly chatId?: string;
    readonly chatMode?: 'fullscreen';
    readonly clientIds?: Array<string>;
    readonly date?: string;
    readonly from?: string;
    readonly mode?: 'live' | 'sandbox';
    readonly organizationId?: string;
    readonly presentation?: ShiftsPresentation;
    readonly scope?: string;
    readonly shiftId?: string;
    readonly slackWebClaim?: string;
    readonly status?: RouteShiftStatus;
    readonly to?: string;
    readonly view?: CalendarViewMode;
};

/**
 * Every search key, spelled out. A projection annotated with this cannot omit
 * a key by accident: dropping one is written as an explicit `undefined`.
 */
export type CompleteShiftRouteSearch = {
    readonly [Key in ShiftRouteSearchKey]: ShiftRouteSearch[Key] | undefined;
};

export const dateKeySearchParam = z
    .string()
    .refine(isValidDateKey)
    .optional()
    .catch(undefined);
